/**
 * ReactionPicker
 *
 * Quick emoji reaction picker that appears on long-press/hover.
 * Shows commonly used reactions with option to expand.
 */

import { useState } from 'react';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export default function ReactionPicker({ onSelect, onClose }) {
  return (
    <div className="flex items-center gap-1 bg-base-100 rounded-full shadow-lg border border-base-300 px-2 py-1 z-20">
      {QUICK_REACTIONS.map(emoji => (
        <button
          key={emoji}
          className="btn btn-ghost btn-xs btn-circle text-base hover:scale-125 transition-transform"
          onClick={() => {
            onSelect(emoji);
            onClose?.();
          }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

/**
 * ReactionDisplay
 *
 * Shows reactions on a message as small badges.
 * Tap → add reaction. Long-press (500ms hold) → remove your reaction.
 */
import { useRef, useCallback } from 'react';

export function ReactionDisplay({ reactions, currentUserId, onAdd, onRemove }) {
  if (!reactions || Object.keys(reactions).length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2 -mb-0.5">
      {Object.entries(reactions).map(([emoji, userIds]) => {
        if (!userIds || userIds.length === 0) return null;
        const hasReacted = userIds.includes(currentUserId);
        const count = userIds.length;

        return (
          <ReactionBadge
            key={emoji}
            emoji={emoji}
            count={count}
            hasReacted={hasReacted}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        );
      })}
    </div>
  );
}

function ReactionBadge({ emoji, count, hasReacted, onAdd, onRemove }) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation();
    firedRef.current = false;
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      if (hasReacted) onRemove?.(emoji);
    }, 500);
  }, [emoji, hasReacted, onRemove]);

  const handlePointerUp = useCallback((e) => {
    e.stopPropagation();
    clearTimeout(timerRef.current);
    if (!firedRef.current) {
      // Short tap → add
      onAdd?.(emoji);
    }
  }, [emoji, onAdd]);

  const handlePointerLeave = useCallback((e) => {
    e.stopPropagation();
    clearTimeout(timerRef.current);
  }, []);

  return (
    <button
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors select-none touch-none ${
        hasReacted
          ? 'bg-white/25 backdrop-blur-sm'
          : 'bg-black/10 hover:bg-black/15'
      }`}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span>{emoji}</span>
      {count > 1 && <span className="text-[11px] font-medium opacity-80">{count}</span>}
    </button>
  );
}
